import { renderToStaticMarkup } from "react-dom/server";
import BaseListPresenter from "../../base/BaseListPresenter";
import { formatDate } from "../../components/common/Converter";
import { findObjectUseCase } from "../../usecases/object";
import PrintPDSTable, {
  PDS_PRINT_STYLES,
} from "./components/printPDS/PrintPDSTable";

// Helper functions for formatting
const formatFullName = (first = "", middle = "", last = "-") =>
  `${first} ${middle} ${last}`.trim();
const formatAddress = ({
  residentialHouseNo = "",
  street = "",
  subDivision = "",
  recidentialBrangay = "",
  recidentialCity = "",
  recidentialProvince = "",
  recidentialRegion = "",
}) =>
  `${residentialHouseNo} ${street} ${subDivision} ${recidentialBrangay} ${recidentialCity} ${recidentialProvince} ${recidentialRegion}`.trim();

const RESIDENTIAL_TO_PERMANENT = {
  residentialHouseNo: "permanentHouseNo",
  residentialZipCode: "permanentZipCode",
  residentialStreet: "permanentStreet",
  residentialSubdivision: "permanentSubdivision",
  recidentialRegion: "permanentRegion",
  recidentialProvince: "permanentProvince",
  recidentialCity: "permanentCity",
  recidentialBrangay: "permanentBrangay",
};

const mirrorResidentialToPermanent = (employee) => {
  if (!employee?.bool) return employee;

  const mirrored = { ...employee };
  Object.entries(RESIDENTIAL_TO_PERMANENT).forEach(
    ([residentialField, permanentField]) => {
      if (employee[residentialField] !== undefined) {
        mirrored[permanentField] = employee[residentialField];
      }
    },
  );
  return mirrored;
};

const formatPermanentAddress = (employee) => {
  const source = mirrorResidentialToPermanent(employee);
  if (employee?.bool) {
    return formatAddress(source);
  }
  return formatAddress({
    residentialHouseNo: source?.permanentHouseNo,
    street: source?.permanentStreet,
    subDivision: source?.permanentSubdivision,
    recidentialBrangay: source?.permanentBrangay,
    recidentialCity: source?.permanentCity,
    recidentialProvince: source?.permanentProvince,
    recidentialRegion: source?.permanentRegion,
  });
};

class ProfilePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }

  init() {
    this.limit = 20;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = ["position", "employee"];
    this.keys = undefined;
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  async employeeUpdate(getObjectId, currentUser) {
    const objectId = getObjectId();
    const isCurrentUser = !objectId;
    const employeeId = isCurrentUser ? currentUser.employee.id : objectId;
    const employees = await findObjectUseCase().execute("employees", {
      where: { id: employeeId },
      include: ["position"],
    });
    const employee = employees[0];
    if (!employee) return;

    const query = { where: { empID: employee.id } };
    const [
      educationalBackground,
      workExperienceBackground,
      voluntaryWorkBackground,
      learningDevBackground,
      civilServiceBackground,
      otherInformationBackground,
      familyBackground,
      referenceBackground,
      ...rest
    ] = await Promise.all([
      findObjectUseCase().execute("educational_background", query),
      findObjectUseCase().execute("work_experience", query),
      findObjectUseCase().execute("voluntary_work", query),
      findObjectUseCase().execute("learning_development", query),
      findObjectUseCase().execute("service_eligibility", query),
      findObjectUseCase().execute("other_information", query),
      findObjectUseCase().execute("family_background", query),
      findObjectUseCase().execute("reference_table", query),
      ...(isCurrentUser
        ? [
            findObjectUseCase().execute("committee", {
              include: ["committeeRelation", "committeeRelation.Designation"],
            }),
          ]
        : [
            findObjectUseCase().execute("users"),
            findObjectUseCase().execute("roles"),
            findObjectUseCase().execute("committee", {
              include: ["committeeRelation", "committeeRelation.Designation"],
            }),
          ]),
    ]);

    let roles = [];
    let filterCommittee = [];
    if (isCurrentUser) {
      roles = currentUser.roles.map((item) => item.id);
      const committeeBackground = rest[0] || [];
      filterCommittee = committeeBackground.filter((object) =>
        object.committeeRelation.some(
          (relation) => relation.employee.id === employee.id,
        ),
      );
      this.view.setState(
        this._defaultState(
          employee,
          educationalBackground,
          workExperienceBackground,
          voluntaryWorkBackground,
          learningDevBackground,
          civilServiceBackground,
          otherInformationBackground,
          familyBackground,
          referenceBackground,
          roles,
          filterCommittee,
        ),
      );
      return;
    } else {
      const users = rest[0] || [];
      const allRoles = rest[1] || [];
      const committeeBackground = rest[2] || [];

      const userACC =
        users.find((item) => item?.employee?.id === employee?.id) || {};
      roles =
        Array.isArray(userACC.roles) && userACC.roles.length > 0
          ? userACC.roles
              .map((item) => {
                const found = allRoles.find((role) => role.id === item.id);
                return found ? found.name : null;
              })
              .filter(Boolean)
          : [];
      filterCommittee = committeeBackground.filter((object) =>
        object.committeeRelation.some(
          (relation) => relation.employee.id === employee.id,
        ),
      );
    }

    // Format names and addresses
    const spouseName = formatFullName(
      employee.spousefirstName,
      employee.spousemiddleName,
      employee.spousesurName,
    );
    const motherName = formatFullName(
      employee.motherName,
      employee.mothermiddleName,
      employee.mothermaidenName,
    );
    const fatherName = formatFullName(
      employee.fatherName,
      employee.fathermiddleName,
      employee.fathersurName,
    );
    const residentialAdd = formatAddress(employee);
    const permanentAdd = formatPermanentAddress(employee);

    this.view.setState({
      employee,
      employeeName: formatFullName(
        employee.Firstname,
        employee.Middlename,
        employee.surname,
      ),
      position: employee.position,
      employeeNo: employee.agency,
      hired: formatDate(employee.hired) || "-",
      email: employee.email || "-",
      telmob: `${employee.mobile || "-"} / ${employee.telephone || "-"}`,
      birthDate: formatDate(employee.birthDate) || "-",
      birthPlace: employee.birthPlace || "-",
      sex: employee.sex || "-",
      civil: employee.civil || "-",
      citizenship: employee.citizenship || "-",
      height: employee.height || "-",
      weight: employee.weight || "-",
      blood: employee.blood || "-",
      tin: employee.tin || "-",
      gsis: employee.gsis || "-",
      sss: employee.sss || "-",
      philhealth: employee.philhealth || "-",
      pagIbig: employee.pagIbig || "-",
      residentialAdd: residentialAdd || "-",
      permanentAdd: permanentAdd || "-",
      picture: employee.picture,
      educationalBackground,
      workExperienceBackground,
      spouseName: spouseName || "-",
      spouseoccupation: employee.spouseoccupation || "-",
      spouseemployer: employee.spouseemployer || "-",
      spouseaddress: employee.spouseaddress || "-",
      spousetelNo: employee.spousetelephone || "-",
      motherName: motherName || "-",
      fatherName: fatherName || "-",
      voluntaryWorkBackground,
      learningDevBackground,
      civilServiceBackground,
      otherInformationBackground,
      personalDataSheet: employee.personalDataSheet,
      appointment: employee.appointment,
      positionDescriptionFrom: employee.positionDescriptionFrom,
      oathOfOffice: employee.oathOfOffice,
      serviceRecord: employee.serviceRecord,
      AssumptionOfDuty: employee.AssumptionOfDuty,
      contractOfService: employee.contractOfService,
      copiesOfCertificatedEligibilities:
        employee.copiesOfCertificatedEligibilities,
      copiesOfDiploma: employee.copiesOfDiploma,
      copyOfBirth: employee.copyOfBirth,
      medicalCertificated: employee.medicalCertificated,
      nbiClearance: employee.nbiClearance,
      NoticeOfSalaryAdjustments: employee.NoticeOfSalaryAdjustments,
      personalOrders: employee.personalOrders,
      letterDirective: employee.letterDirective,
      travelOrders: employee.travelOrders,
      copiesOfDisciplinaryActions: employee.copiesOfDisciplinaryActions,
      individualPerformance: employee.individualPerformance,
      swornStatementOfAssets: employee.swornStatementOfAssets,
      othersFile: employee.othersFile,
      familyBackground,
      referenceBackground,
      roles,
      filterCommittee: filterCommittee || [],
    });
  }

  _defaultState(
    currentUser,
    educationalBackground,
    workExperienceBackground,
    voluntaryWorkBackground,
    learningDevBackground,
    civilServiceBackground,
    otherInformationBackground,
    familyBackground,
    referenceBackground,
    roles,
    filterCommittee,
  ) {
    const residentialAdd = formatAddress(currentUser);
    const permanentAdd = formatPermanentAddress(currentUser);
    const spouseName = formatFullName(
      currentUser.spousefirstName,
      currentUser.spousemiddleName,
      currentUser.spousesurName,
    );
    const motherName = formatFullName(
      currentUser.motherName,
      currentUser.mothermiddleName,
      currentUser.mothermaidenName,
    );
    const fatherName = formatFullName(
      currentUser.fatherName,
      currentUser.fathermiddleName,
      currentUser.fathersurName,
    );
    return {
      employeeName:
        formatFullName(
          currentUser.Firstname,
          currentUser.Middlename,
          currentUser.surname,
        ) || "-",
      position: currentUser.position || "-",
      employeeNo: currentUser.agency || "-",
      hired: formatDate(currentUser.hired) || "-",
      email: currentUser.email,
      telmob: `${currentUser.mobile || "-"} / ${currentUser.telephone || "-"}`,
      birthDate: formatDate(currentUser.birthDate) || "-",
      birthPlace: currentUser.birthPlace || "-",
      sex: currentUser.sex || "-",
      civil: currentUser.civil || "-",
      citizenship: currentUser.citizenship || "-",
      height: currentUser.height || "-",
      weight: currentUser.weight || "-",
      blood: currentUser.blood || "-",
      tin: currentUser.tin || "-",
      gsis: currentUser.gsis || "-",
      sss: currentUser.sss || "-",
      philhealth: currentUser.philhealth || "-",
      pagIbig: currentUser.pagIbig || "-",
      residentialAdd: residentialAdd || "-",
      permanentAdd: permanentAdd || "-",
      picture: currentUser.picture || "",
      workExperienceBackground: workExperienceBackground || [],
      spouseName: spouseName || "-",
      spouseoccupation: currentUser.spouseoccupation || "-",
      spouseemployer: currentUser.spouseemployer || "-",
      spouseaddress: currentUser.spouseaddress || "-",
      spousetelNo: currentUser.spousetelephone || "-",
      motherName: motherName || "-",
      fatherName: fatherName || "-",
      voluntaryWorkBackground: voluntaryWorkBackground || [],
      learningDevBackground: learningDevBackground || [],
      civilServiceBackground: civilServiceBackground || [],
      otherInformationBackground: otherInformationBackground || [],
      personalDataSheet: currentUser.personalDataSheet,
      appointment: currentUser.appointment,
      positionDescriptionFrom: currentUser.positionDescriptionFrom,
      oathOfOffice: currentUser.oathOfOffice,
      serviceRecord: currentUser.serviceRecord,
      AssumptionOfDuty: currentUser.AssumptionOfDuty,
      contractOfService: currentUser.contractOfService,
      copiesOfCertificatedEligibilities:
        currentUser.copiesOfCertificatedEligibilities,
      copiesOfDiploma: currentUser.copiesOfDiploma,
      copyOfBirth: currentUser.copyOfBirth,
      medicalCertificated: currentUser.medicalCertificated,
      nbiClearance: currentUser.nbiClearance,
      NoticeOfSalaryAdjustments: currentUser.NoticeOfSalaryAdjustments,
      personalOrders: currentUser.personalOrders,
      letterDirective: currentUser.letterDirective,
      travelOrders: currentUser.travelOrders,
      copiesOfDisciplinaryActions: currentUser.copiesOfDisciplinaryActions,
      individualPerformance: currentUser.individualPerformance,
      swornStatementOfAssets: currentUser.swornStatementOfAssets,
      othersFile: currentUser.othersFile,
      employee: currentUser || [],
      familyBackground: familyBackground || [],
      educationalBackground: educationalBackground || [],
      referenceBackground: referenceBackground || [],
      roles: roles || [],
      filterCommittee: filterCommittee || [],
    };
  }

  async PrintPDS() {
    const object = this.view.state;
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;

    const printContentElement = PrintPDSTable({
      object: object,
    });
    const printContentHTML = renderToStaticMarkup(printContentElement);

    iframeDoc.open();
    iframeDoc.write(
      `<html><head><title>Print PDS</title><style>${PDS_PRINT_STYLES}</style></head><body>`,
    );
    iframeDoc.write(printContentHTML);
    iframeDoc.write("</body></html>");
    iframeDoc.close();

    iframe.onload = () => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  }
}

export default ProfilePresenter;
